import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Save, Video, ImageIcon, Plus, Tag, ChevronDown } from "lucide-react";
import { ICON_CATALOG, iconByName, amenityIcon, labelOf } from "@/lib/amenityIcons";

/**
 * PropertyEditor — admin UI for editing a single property in depth.
 */
export default function PropertyEditor({ slug, onClose, onSaved }) {
  const [prop, setProp] = useState(null);
  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [seoMaster, setSeoMaster] = useState([]);
  const [saving, setSaving] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [newSeoTag, setNewSeoTag] = useState("");

  useEffect(() => {
    api.get(`/properties/${slug}`).then(({ data }) => setProp(data));
    api.get("/collections").then(({ data }) => setCollections(data));
    api.get("/locations").then(({ data }) => setLocations(data));
    api.get("/admin/seo-tags").then(({ data }) => setSeoMaster(data));
  }, [slug]);

  if (!prop) return <div className="p-10 text-center text-noir/60">Loading…</div>;

  const allPhotos = [prop.hero_image, ...(prop.gallery || [])].filter(Boolean);
  const topIdxs = prop.top_photo_indexes || [];
  const statsOverride = prop.stats_override || {};

  const setOverride = (k, v) =>
    setProp({ ...prop, stats_override: { ...(prop.stats_override || {}), [k]: v } });

  const togglePhotoInTop = (i) => {
    const next = topIdxs.includes(i) ? topIdxs.filter((x) => x !== i) : [...topIdxs, i];
    setProp({ ...prop, top_photo_indexes: next });
  };
  const movePhoto = (idx, dir) => {
    const next = [...topIdxs];
    const pos = next.indexOf(idx);
    if (pos < 0) return;
    const swap = pos + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[pos], next[swap]] = [next[swap], next[pos]];
    setProp({ ...prop, top_photo_indexes: next });
  };

  const toggleSeoTag = (label) => {
    const cur = prop.seo_tags || [];
    const next = cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label];
    setProp({ ...prop, seo_tags: next });
  };
  const addSeoTag = async () => {
    const label = newSeoTag.trim();
    if (!label) return;
    await api.post("/admin/seo-tags", { label });
    const { data } = await api.get("/admin/seo-tags");
    setSeoMaster(data);
    setNewSeoTag("");
    if (!(prop.seo_tags || []).includes(label)) {
      setProp({ ...prop, seo_tags: [...(prop.seo_tags || []), label] });
    }
  };

  const uploadFile = async (file, target) => {
    if (target === "video") {
      const duration = await new Promise((resolve) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => resolve(v.duration || 0);
        v.onerror = () => resolve(0);
        v.src = URL.createObjectURL(file);
      });
      if (duration > 120.5) {
        toast.error(`Video is ${Math.round(duration)}s — please upload something 2 minutes or shorter, or paste a YouTube link instead.`);
        return;
      }
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    if (target === "video") {
      setProp({ ...prop, videos: [...(prop.videos || []), data.url] });
    } else if (target === "hero") {
      setProp({ ...prop, hero_image_override: data.url });
    }
    toast.success("Uploaded");
  };

  const addVideoUrl = () => {
    const v = videoUrl.trim();
    if (!v) return;
    if (!/^https?:\/\//.test(v)) { toast.error("Please include the full URL including https://"); return; }
    setProp({ ...prop, videos: [...(prop.videos || []), v] });
    setVideoUrl("");
    toast.success("Added");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        display_name: prop.display_name,
        tagline: prop.tagline,
        short_description: prop.short_description,
        listing_description: prop.listing_description,
        space: prop.space,
        interaction: prop.interaction,
        highlights: prop.highlights || [],
        amenities: prop.amenities || [],
        bedrooms: Number(prop.bedrooms) || 1,
        bathrooms: Number(prop.bathrooms) || 1,
        guests: Number(prop.guests) || 2,
        floor: prop.floor,
        collections: prop.collections || [],
        location_slug: prop.location_slug,
        location_label: prop.location_label,
        featured: !!prop.featured,
        guest_favourite: !!prop.guest_favourite,
        nightly_price_from: Number(prop.nightly_price_from) || 0,
        top_photo_indexes: prop.top_photo_indexes || [],
        videos: prop.videos || [],
        hero_image_override: prop.hero_image_override || "",
        house_rules_html: prop.house_rules_html || "",
        airbnb_markup_pct_override: prop.airbnb_markup_pct_override ?? null,
        website_markup_pct: prop.website_markup_pct ?? null,
        airbnb_url: prop.airbnb_url || "",
        seo_tags: prop.seo_tags || [],
        location_about_html: prop.location_about_html || "",
        stats_override: prop.stats_override || {},
      };
      await api.patch(`/admin/properties/${slug}`, payload);
      toast.success("Saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="property-editor" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl">Edit · {prop.display_name}</h2>
          <p className="text-xs text-noir/60">{prop.slug} · hostify:{prop.hostify_listing_id || "—"}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="vyg-pill" data-testid="prop-editor-back">← Back</button>
          <button onClick={save} disabled={saving} className="vyg-cta" data-testid="prop-editor-save">
            <Save size={14}/> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Basic */}
      <section className="grid gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:grid-cols-2">
        <Field label="Display name"><input value={prop.display_name || ""} onChange={(e) => setProp({ ...prop, display_name: e.target.value })} className="inp" /></Field>
        <Field label="Tagline"><input value={prop.tagline || ""} onChange={(e) => setProp({ ...prop, tagline: e.target.value })} className="inp" /></Field>
        <Field label="Short description (cards, meta)" className="sm:col-span-2"><textarea rows={2} value={prop.short_description || ""} onChange={(e) => setProp({ ...prop, short_description: e.target.value })} className="inp" /></Field>
        <Field label="Listing description (‘About this stay’)" className="sm:col-span-2"><textarea rows={5} value={prop.listing_description || ""} onChange={(e) => setProp({ ...prop, listing_description: e.target.value })} data-testid="listing-description-input" className="inp" /></Field>
        <Field label="The Space" className="sm:col-span-2"><textarea rows={4} value={prop.space || ""} onChange={(e) => setProp({ ...prop, space: e.target.value })} data-testid="space-input" className="inp" /></Field>
        <Field label="Host interaction" className="sm:col-span-2"><textarea rows={3} value={prop.interaction || ""} onChange={(e) => setProp({ ...prop, interaction: e.target.value })} data-testid="interaction-input" className="inp" /></Field>
        <Field label="Bedrooms"><input type="number" value={prop.bedrooms || 1} onChange={(e) => setProp({ ...prop, bedrooms: Number(e.target.value) })} className="inp" /></Field>
        <Field label="Bathrooms"><input type="number" value={prop.bathrooms || 1} onChange={(e) => setProp({ ...prop, bathrooms: Number(e.target.value) })} className="inp" /></Field>
        <Field label="Max guests"><input type="number" value={prop.guests || 2} onChange={(e) => setProp({ ...prop, guests: Number(e.target.value) })} className="inp" /></Field>
        <Field label="Floor / level"><input value={prop.floor || ""} onChange={(e) => setProp({ ...prop, floor: e.target.value })} className="inp" /></Field>
        <Field label="Nightly price (INR, fallback)"><input type="number" value={prop.nightly_price_from || 0} onChange={(e) => setProp({ ...prop, nightly_price_from: Number(e.target.value) })} className="inp" /></Field>
        <Field label="Airbnb markup override (%) — blank = inherit"><input type="number" value={prop.airbnb_markup_pct_override ?? ""} onChange={(e) => setProp({ ...prop, airbnb_markup_pct_override: e.target.value === "" ? null : Number(e.target.value) })} className="inp" /></Field>
        <Field label="Website markup override (%) — blank = inherit global">
          <input
            type="number"
            step="0.5"
            value={prop.website_markup_pct ?? ""}
            onChange={(e) => setProp({ ...prop, website_markup_pct: e.target.value === "" ? null : Number(e.target.value) })}
            data-testid="prop-website-markup"
            placeholder="e.g. 8 → +8% on Hostify price for this property"
            className="inp"
          />
        </Field>
        <Field label="Airbnb listing URL (for ‘Compare Airbnb Price’ button)" className="sm:col-span-2">
          <input value={prop.airbnb_url || ""} onChange={(e) => setProp({ ...prop, airbnb_url: e.target.value.trim() })}
            data-testid="airbnb-url-input"
            placeholder="https://www.airbnb.com/rooms/XXXXXXXX"
            className="inp" />
        </Field>
        <div className="flex items-center gap-4 text-sm sm:col-span-2">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!prop.featured} onChange={(e) => setProp({ ...prop, featured: e.target.checked })} /> Featured on home</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!prop.guest_favourite} onChange={(e) => setProp({ ...prop, guest_favourite: e.target.checked })} /> Guest favourite</label>
        </div>
      </section>

      {/* Highlights — shown in "Good to know" */}
      <ListEditor
        title="Highlights (‘Good to know’)"
        subtitle="One-line bullets that show up on the property page below The Space."
        items={prop.highlights || []}
        onChange={(next) => setProp({ ...prop, highlights: next })}
        placeholder="e.g. 2 mins walk to Dhan Mill café"
        testId="highlights"
      />

      {/* Amenities */}
      <ListEditor
        title="Top amenities"
        subtitle="Featured amenities that appear on the property card + in the amenities drawer. Hostify sync also writes into this list."
        items={prop.amenities || []}
        onChange={(next) => setProp({ ...prop, amenities: next })}
        placeholder="e.g. 200 Mbps Wi-Fi"
        testId="amenities"
      />

      {/* Per-property FAQs */}
      <PropertyFaqs slug={prop.slug} />

      {/* SEO / Suitability tags */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5" data-testid="seo-tags-section">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-display text-lg flex items-center gap-2"><Tag size={16}/> SEO tags (shown at top of property page)</p>
            <p className="text-xs text-noir/60 mt-1">Pick from the master list. Add new ones if you need a tag that doesn't exist yet.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {seoMaster.map((t) => {
            const active = (prop.seo_tags || []).includes(t.label);
            return (
              <button key={t.label} type="button" onClick={() => toggleSeoTag(t.label)}
                data-testid={`seo-tag-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "border-forest bg-forest text-parchment" : "border-forest/20 bg-mist/30 text-noir/80 hover:bg-mist/60"}`}>
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex gap-2">
          <input value={newSeoTag} onChange={(e) => setNewSeoTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSeoTag())}
            placeholder="New tag (e.g. Pet-friendly)"
            data-testid="new-seo-tag-input"
            className="inp flex-1 max-w-sm" />
          <button type="button" onClick={addSeoTag} className="vyg-pill" data-testid="add-seo-tag"><Plus size={14}/> Add to master list</button>
        </div>
      </section>

      {/* Location */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5">
        <p className="font-display text-lg">Location tag</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {locations.map((l) => {
            const active = prop.location_slug === l.slug;
            return (
              <button key={l.slug} type="button" onClick={() => setProp({ ...prop, location_slug: l.slug, location_label: l.name })}
                data-testid={`location-tag-${l.slug}`}
                className={`rounded-full border px-3 py-1.5 text-sm ${active ? "border-forest bg-forest text-parchment" : "border-forest/20 bg-mist/30 text-noir/80"}`}>
                {l.name}
              </button>
            );
          })}
        </div>
        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wider text-amber">About the location (shown on property page · HTML allowed)</p>
          <textarea rows={5} value={prop.location_about_html || ""}
            onChange={(e) => setProp({ ...prop, location_about_html: e.target.value })}
            data-testid="location-about-input"
            placeholder="<p>Just a 3-minute walk from the Dhan Mill café strip, this stay…</p>"
            className="inp mt-2 font-mono text-xs" />
        </div>
      </section>

      {/* Collections */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5">
        <p className="font-display text-lg">Collection tags</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {collections.map((c) => {
            const active = (prop.collections || []).includes(c.slug);
            return (
              <button key={c.slug} type="button" onClick={() => {
                const has = (prop.collections || []).includes(c.slug);
                const next = has ? (prop.collections || []).filter((x) => x !== c.slug) : [...(prop.collections || []), c.slug];
                setProp({ ...prop, collections: next });
              }} data-testid={`collection-tag-${c.slug}`}
                className={`rounded-full border px-3 py-1.5 text-sm ${active ? "border-forest bg-forest text-parchment" : "border-forest/20 bg-mist/30 text-noir/80"}`}>
                {c.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* Per-property social stats */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5" data-testid="property-stats-section">
        <p className="font-display text-lg">Social validation (this unit)</p>
        <p className="text-xs text-noir/60 mt-1">
          Total stays is admin-entered. Reviews, 5★ count and average rating are auto-computed
          from the reviews collection — override them only when you must.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Total stays (on this unit)">
            <input type="number" value={statsOverride.total_stays ?? ""} onChange={(e) => setOverride("total_stays", e.target.value === "" ? null : Number(e.target.value))}
              data-testid="stats-total-stays" className="inp" placeholder="e.g. 148" />
          </Field>
          <Field label={`Total reviews (computed: ${prop.stats_total_reviews ?? 0}) — override`}>
            <input type="number" value={statsOverride.total_reviews ?? ""} onChange={(e) => setOverride("total_reviews", e.target.value === "" ? null : Number(e.target.value))}
              data-testid="stats-total-reviews" className="inp" placeholder="Leave blank = use computed" />
          </Field>
          <Field label={`5★ reviews (computed: ${prop.stats_five_star ?? 0}) — override`}>
            <input type="number" value={statsOverride.five_star ?? ""} onChange={(e) => setOverride("five_star", e.target.value === "" ? null : Number(e.target.value))}
              data-testid="stats-five-star" className="inp" placeholder="Leave blank = use computed" />
          </Field>
          <Field label={`Avg rating (computed: ${prop.stats_avg_rating ?? 0}) — override`}>
            <input type="number" step="0.01" value={statsOverride.avg_rating ?? ""} onChange={(e) => setOverride("avg_rating", e.target.value === "" ? null : Number(e.target.value))}
              data-testid="stats-avg-rating" className="inp" placeholder="Leave blank = use computed" />
          </Field>
        </div>
      </section>

      {/* Hero image override */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display text-lg">Hero image override</p>
            <p className="text-xs text-noir/60">Upload a custom photo for this property's hero/card, or pick one from the gallery below.</p>
          </div>
          <label className="vyg-pill cursor-pointer">
            <ImageIcon size={14}/> Upload
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "hero")} />
          </label>
        </div>
        {prop.hero_image_override && (
          <div className="mt-3 flex items-center gap-3">
            <img src={prop.hero_image_override} alt="" className="h-16 w-24 rounded object-cover" />
            <button onClick={() => setProp({ ...prop, hero_image_override: "" })} className="text-xs text-red-600">Remove override</button>
          </div>
        )}
      </section>

      {/* Top photos picker */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5">
        <p className="font-display text-lg">Top photos (carousel + detail hero)</p>
        <p className="text-xs text-noir/60 mt-1">Tap to toggle. Use ▲/▼ to reorder. Order here = order guests see when browsing cards and the property detail gallery.</p>

        {topIdxs.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider text-amber mb-2">Selected ({topIdxs.length})</p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-6">
              {topIdxs.map((i, pos) => (
                <div key={i} data-testid={`top-photo-${i}`} className="relative rounded-lg overflow-hidden border-2 border-forest">
                  <img src={allPhotos[i]} alt="" className="aspect-[4/3] w-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 flex justify-between bg-noir/70 p-1 text-xs text-white">
                    <span>#{pos + 1}</span>
                    <div className="flex gap-1">
                      <button onClick={() => movePhoto(i, -1)} className="px-1">▲</button>
                      <button onClick={() => movePhoto(i, 1)} className="px-1">▼</button>
                      <button onClick={() => togglePhotoInTop(i)} className="px-1">×</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wider text-amber mb-2">All photos ({allPhotos.length})</p>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-6">
            {allPhotos.map((src, i) => {
              const selected = topIdxs.includes(i);
              return (
                <button key={i} onClick={() => togglePhotoInTop(i)}
                  data-testid={`photo-picker-${i}`}
                  className={`group relative rounded-lg overflow-hidden border-2 ${selected ? "border-forest" : "border-transparent"}`}>
                  <img src={src} alt="" className="aspect-[4/3] w-full object-cover transition group-hover:opacity-90" />
                  {selected && <span className="absolute right-1 top-1 rounded-full bg-forest px-1.5 py-0.5 text-[10px] text-parchment">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Videos */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display text-lg">Property videos</p>
            <p className="text-xs text-noir/60 mt-1">Uploaded MP4/MOV (2 minutes max) or a YouTube/Vimeo link. Videos appear in the hero carousel and Photo Explorer with a play overlay.</p>
          </div>
          <label className="vyg-pill cursor-pointer" data-testid="upload-video">
            <Upload size={14}/> Upload (≤2 min)
            <input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "video")} />
          </label>
        </div>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="Paste YouTube or Vimeo link — e.g. https://youtu.be/xxxxxxxxxxx"
            data-testid="video-url-input"
            className="inp flex-1" />
          <button onClick={addVideoUrl} className="vyg-pill" data-testid="add-video-url"><Plus size={14}/> Add link</button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {(prop.videos || []).map((v, i) => {
            const isYT = /youtube\.com|youtu\.be/i.test(v);
            const isVimeo = /vimeo\.com/i.test(v);
            const isExternal = isYT || isVimeo;
            const label = isYT ? "YouTube" : isVimeo ? "Vimeo" : "Uploaded";
            return (
              <div key={v || `vid-${i}`} data-testid={`video-${i}`} className="rounded-lg overflow-hidden border border-forest/10 bg-noir/5">
                {!isExternal ? (
                  <video src={v} controls className="aspect-video w-full" />
                ) : (
                  <div className="flex aspect-video items-center justify-center bg-noir/80 text-parchment text-xs">
                    {label} · preview on the live site
                  </div>
                )}
                <div className="flex items-center justify-between p-2 text-xs">
                  <span className="truncate"><Video size={12} className="inline mr-1"/> {label}</span>
                  <button onClick={() => setProp({ ...prop, videos: prop.videos.filter((_, j) => j !== i) })} className="text-red-600"><Trash2 size={12}/></button>
                </div>
              </div>
            );
          })}
          {(!prop.videos || prop.videos.length === 0) && <p className="col-span-full text-sm text-noir/50">No videos yet.</p>}
        </div>
      </section>

      {/* House rules */}
      <section className="rounded-2xl border border-forest/10 bg-white p-5">
        <p className="font-display text-lg">House rules (per-property popup)</p>
        <p className="text-xs text-noir/60 mt-1">Shown on the property detail page via the "House rules" popup. Plain text or simple HTML allowed.</p>
        <textarea rows={10} value={prop.house_rules_html || ""} onChange={(e) => setProp({ ...prop, house_rules_html: e.target.value })}
          data-testid="house-rules-input"
          className="inp mt-3 font-mono text-xs" />
      </section>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} className="vyg-cta shadow-soft"><Save size={14}/>{saving ? "Saving…" : "Save property"}</button>
      </div>

      <style>{`.inp{width:100%;border:1px solid rgb(31 71 58 / .2);border-radius:0.5rem;padding:0.55rem 0.75rem;background:#fff;font-size:0.875rem;outline:none}.inp:focus{border-color:#1F473A}`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-wider text-amber">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/**
 * ListEditor — simple add/remove list with an optional icon picker per item.
 *
 * Items can be stored as a plain string (legacy) OR a structured object
 * `{ label, icon }`. The editor normalises everything to objects internally
 * while preserving existing plain-string entries when the admin hasn't picked
 * a custom icon yet — so older data renders exactly as before.
 */
function ListEditor({ title, subtitle, items, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  const norm = (x) => (typeof x === "string" ? { label: x, icon: "" } : { label: x.label || "", icon: x.icon || "" });
  const pack = (arr) => arr.map((x) => (x.icon ? { label: x.label, icon: x.icon } : x.label));
  const rows = (items || []).map(norm);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange(pack([...rows, { label: v, icon: "" }]));
    setDraft("");
  };
  const setLabel = (i, label) => {
    const next = [...rows]; next[i] = { ...next[i], label };
    onChange(pack(next));
  };
  const setIcon = (i, icon) => {
    const next = [...rows]; next[i] = { ...next[i], icon };
    onChange(pack(next));
  };
  const remove = (i) => onChange(pack(rows.filter((_, j) => j !== i)));
  const move = (i, dir) => {
    const next = [...rows]; const swap = i + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[i], next[swap]] = [next[swap], next[i]];
    onChange(pack(next));
  };

  return (
    <section className="rounded-2xl border border-forest/10 bg-white p-5" data-testid={`list-editor-${testId}`}>
      <p className="font-display text-lg">{title}</p>
      {subtitle && <p className="text-xs text-noir/60 mt-1">{subtitle}</p>}
      <ul className="mt-4 space-y-2">
        {rows.map((it, i) => {
          const Icon = amenityIcon(it);
          return (
            <li key={i} className="flex items-center gap-2 rounded-lg border border-forest/10 bg-mist/30 px-3 py-2 text-sm" data-testid={`${testId}-row-${i}`}>
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-forest shadow-card" aria-hidden>
                <Icon size={14} />
              </span>
              <input
                value={it.label}
                onChange={(e) => setLabel(i, e.target.value)}
                className="inp !py-1.5 flex-1 min-w-0"
                data-testid={`${testId}-label-${i}`}
                placeholder={placeholder}
              />
              <IconPicker value={it.icon} onChange={(v) => setIcon(i, v)} testId={`${testId}-icon-${i}`} />
              <button onClick={() => move(i, -1)} className="text-xs px-1 text-noir/60" aria-label="Move up">▲</button>
              <button onClick={() => move(i, 1)} className="text-xs px-1 text-noir/60" aria-label="Move down">▼</button>
              <button onClick={() => remove(i)} className="text-red-600 hover:bg-red-50 p-1 rounded" aria-label="Remove"><Trash2 size={13}/></button>
            </li>
          );
        })}
        {rows.length === 0 && <li className="text-sm text-noir/50">Nothing here yet.</li>}
      </ul>
      <div className="mt-3 flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder} data-testid={`${testId}-input`}
          className="inp flex-1" />
        <button type="button" onClick={add} data-testid={`${testId}-add`} className="vyg-pill"><Plus size={14}/> Add</button>
      </div>
    </section>
  );
}

/**
 * IconPicker — dropdown button that shows the current icon, opens a grid of
 * lucide icons the admin can pick from. "Auto" restores keyword-based matching.
 */
function IconPicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const Current = value ? iconByName(value) : null;

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full border border-forest/20 bg-white px-2.5 py-1 text-[11px] uppercase tracking-wider text-forest hover:bg-mist/60"
        data-testid={testId}
        aria-expanded={open}
        title={value ? `Icon: ${value}` : "Icon: auto-matched"}
      >
        {Current ? <Current size={13}/> : <span className="text-noir/50">Auto</span>}
        <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-72 rounded-xl border border-forest/15 bg-white p-3 shadow-soft" data-testid={`${testId}-panel`}>
          <div className="flex items-center justify-between pb-2 text-[11px] uppercase tracking-wider text-amber">
            <span>Pick an icon</span>
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="text-forest hover:underline"
              data-testid={`${testId}-auto`}
            >Use auto</button>
          </div>
          <div className="grid max-h-72 grid-cols-6 gap-1 overflow-y-auto">
            {ICON_CATALOG.map(([name, label]) => {
              const Icon = iconByName(name);
              if (!Icon) return null;
              const selected = name === value;
              return (
                <button
                  key={name}
                  type="button"
                  title={label}
                  onClick={() => { onChange(name); setOpen(false); }}
                  data-testid={`${testId}-opt-${name}`}
                  className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${selected ? "border-forest bg-forest text-parchment" : "border-forest/10 bg-mist/40 text-forest hover:bg-mist/80"}`}
                  aria-label={label}
                >
                  <Icon size={15} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * PropertyFaqs — inline FAQ editor tied to a specific property slug. Reuses the
 * global /api/admin/faqs endpoint with scope="property". Changes persist on their
 * own — no need to click "Save property" afterwards.
 */
function PropertyFaqs({ slug }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ question: "", answer: "", group: "Stay" });

  const load = async () => {
    if (!slug) return;
    const { data } = await api.get(`/admin/faqs`, { params: { scope: "property", property_slug: slug } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  const add = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) { toast.error("Question and answer required"); return; }
    await api.post("/admin/faqs", { scope: "property", property_slug: slug, ...draft, order: items.length });
    setDraft({ question: "", answer: "", group: draft.group || "Stay" });
    load();
  };
  const update = async (id, patch) => { await api.patch(`/admin/faqs/${id}`, patch); load(); };
  const del = async (id) => { if (!window.confirm("Delete this FAQ?")) return; await api.delete(`/admin/faqs/${id}`); load(); };

  return (
    <section className="rounded-2xl border border-forest/10 bg-white p-5" data-testid="property-faqs">
      <p className="font-display text-lg">FAQs (this property)</p>
      <p className="text-xs text-noir/60 mt-1">Appears in the FAQ accordion on the property page. Leave empty to inherit the global defaults.</p>
      <div className="mt-4 space-y-3">
        {items.map((f) => (
          <div key={f.id} className="rounded-lg border border-forest/10 p-3" data-testid={`faq-row-${f.id}`}>
            <input defaultValue={f.question} onBlur={(e) => e.target.value !== f.question && update(f.id, { question: e.target.value })}
              className="w-full bg-transparent font-medium text-sm focus:outline-none" />
            <textarea defaultValue={f.answer} rows={2}
              onBlur={(e) => e.target.value !== f.answer && update(f.id, { answer: e.target.value })}
              className="mt-2 w-full bg-transparent text-sm text-noir/80 focus:outline-none resize-none" />
            <div className="mt-2 flex justify-end">
              <button onClick={() => del(f.id)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-noir/50">No property-specific FAQs yet — the property will show global FAQs.</p>}
      </div>
      <div className="mt-4 grid gap-2">
        <input placeholder="Question" value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })}
          data-testid="new-faq-q" className="inp" />
        <textarea placeholder="Answer" rows={2} value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
          data-testid="new-faq-a" className="inp" />
        <div className="flex gap-2">
          <input placeholder="Group (e.g. Stay, Shoots)" value={draft.group} onChange={(e) => setDraft({ ...draft, group: e.target.value })}
            className="inp flex-1" />
          <button type="button" onClick={add} data-testid="add-faq" className="vyg-pill"><Plus size={14}/> Add FAQ</button>
        </div>
      </div>
    </section>
  );
}
